/**
 * Databases on TanStack Query: one persisted query per database (schema + views + every row, rank order),
 * optimistic mutations queued while offline and replayed in order (one "db" scope, so a row created offline gets
 * its server id before later edits of it are sent), and realtime row/view/database events.
 * Pending writes are re-applied on top of every refetch, so a background refresh never hides queued edits.
 */
import type { CellWrite, CreateRowRequest, DbView, PersistentEvent, Row, SummaryRowResponse, UpdateDatabaseRequest, UpdateRowRequest, UpdateViewRequest, WriteCellsResponse } from "@bundle/contracts";
import { Db, SheetEdit, errorKind, type ApiClient, type DatabaseCache, type NewRowInput, type NewViewInput, type Observable, type RowPatch } from "@bundle/core";
import { MutationObserver, type Mutation, type QueryClient } from "@tanstack/query-core";

export const dbKeys = {
  all: (ws: string) => ["ws", ws, "db"] as const,
  database: (ws: string, databaseId: string) => ["ws", ws, "db", databaseId] as const,
  /** Temporary (clientId) → server ids of rows/views created while offline. */
  ids: (ws: string) => ["ws", ws, "dbIds"] as const,
};

/** Database writes run one at a time, in order. */
export const DB_SCOPE = { id: "db" };

export type CreateRowVars = { kind: "createRow"; ws: string; databaseId: string; meId: string; input: NewRowInput };
export type UpdateRowVars = { kind: "updateRow"; ws: string; databaseId: string; rowId: string; meId: string; patch: RowPatch };
export type DeleteRowVars = { kind: "deleteRow"; ws: string; databaseId: string; rowId: string };
export type UpdateDatabaseVars = { kind: "updateDatabase"; ws: string; databaseId: string; req: UpdateDatabaseRequest };
export type CreateViewVars = { kind: "createView"; ws: string; databaseId: string; input: NewViewInput };
export type UpdateViewVars = { kind: "updateView"; ws: string; databaseId: string; viewId: string; req: UpdateViewRequest };
export type DeleteViewVars = { kind: "deleteView"; ws: string; databaseId: string; viewId: string };
export type WriteCellsVars = { kind: "writeCells"; ws: string; databaseId: string; meId: string; clientId: string; writes: CellWrite[] };
export type CreateSummaryRowVars = { kind: "createSummaryRow"; ws: string; databaseId: string; clientId: string; index?: number };
export type MoveSummaryRowVars = { kind: "moveSummaryRow"; ws: string; databaseId: string; summaryRowId: string; index: number };
export type DeleteSummaryRowVars = { kind: "deleteSummaryRow"; ws: string; databaseId: string; summaryRowId: string };
export type DbVars =
  | CreateRowVars
  | UpdateRowVars
  | DeleteRowVars
  | UpdateDatabaseVars
  | CreateViewVars
  | UpdateViewVars
  | DeleteViewVars
  | WriteCellsVars
  | CreateSummaryRowVars
  | MoveSummaryRowVars
  | DeleteSummaryRowVars;

/** Cell writes with their row / summary row ids resolved (temporary ids of rows created offline). */
function resolveWrites(client: QueryClient, ws: string, writes: CellWrite[]): CellWrite[] {
  return writes.map((w) => {
    const out: CellWrite = { ...w };
    if (w.rowId !== undefined) out.rowId = resolveId(client, ws, w.rowId);
    if (w.summaryRowId !== undefined) out.summaryRowId = resolveId(client, ws, w.summaryRowId);
    return out;
  });
}

const PAGE_LIMIT = 500;
const MAX_PAGES = 40;

// ---------- Cache helpers ----------

function updateDb(client: QueryClient, ws: string, databaseId: string, f: (c: DatabaseCache) => DatabaseCache): void {
  client.setQueryData<DatabaseCache>(dbKeys.database(ws, databaseId), (c) => (c === undefined ? c : f(c)));
}

const NO_IDS: Record<string, string> = {};

function idMap(client: QueryClient, ws: string): Record<string, string> {
  return client.getQueryData<Record<string, string>>(dbKeys.ids(ws)) ?? NO_IDS;
}

export function resolveId(client: QueryClient, ws: string, id: string): string {
  return idMap(client, ws)[id] ?? id;
}

function remember(client: QueryClient, ws: string, tempId: string, id: string): void {
  client.setQueryData<Record<string, string>>(dbKeys.ids(ws), { ...idMap(client, ws), [tempId]: id });
}

/** The optimistic effect of one write (also re-applied on top of refetched data while still pending). */
function applyWrite(client: QueryClient, c: DatabaseCache, v: DbVars): DatabaseCache {
  switch (v.kind) {
    case "createRow":
      return c.rows.some((r) => r.id === v.input.clientId || r.id === resolveId(client, v.ws, v.input.clientId))
        ? c
        : { ...c, rows: Db.upsertRow(c.rows, Db.optimisticRow(v.databaseId, v.input, v.meId)) };
    case "updateRow": {
      const id = resolveId(client, v.ws, v.rowId);
      const row = c.rows.find((r) => r.id === id) ?? c.rows.find((r) => r.id === v.rowId);
      if (row === undefined) return c;
      return { ...c, rows: Db.upsertRow(c.rows, Db.patchRow(row, v.patch.values ?? {}, v.patch.rank, v.patch.icon, v.patch.at, v.meId)) };
    }
    case "deleteRow":
      return { ...c, rows: Db.removeRow(Db.removeRow(c.rows, v.rowId), resolveId(client, v.ws, v.rowId)) };
    case "updateDatabase":
      return { ...c, database: Db.patchDatabase(c.database, v.req) };
    case "createView": {
      const id = resolveId(client, v.ws, v.input.clientId);
      if (c.views.some((x) => x.id === id || x.id === v.input.clientId)) return c;
      const { clientId: _c, ...fields } = v.input.request;
      return { ...c, views: Db.upsertView(c.views, { ...fields, id: v.input.clientId, databaseId: v.databaseId, rank: v.input.rank }) };
    }
    case "updateView": {
      const id = resolveId(client, v.ws, v.viewId);
      const view = c.views.find((x) => x.id === id) ?? c.views.find((x) => x.id === v.viewId);
      if (view === undefined) return c;
      return { ...c, views: Db.upsertView(c.views, Db.patchView(view, v.req)) };
    }
    case "deleteView":
      return { ...c, views: Db.removeView(Db.removeView(c.views, v.viewId), resolveId(client, v.ws, v.viewId)) };
    case "writeCells":
      return SheetEdit.applyCellWrites(c, resolveWrites(client, v.ws, v.writes), Date.now(), v.meId);
    case "createSummaryRow": {
      const id = resolveId(client, v.ws, v.clientId);
      if ((c.database.summaryRows ?? []).some((s) => s.id === id || s.id === v.clientId)) return c;
      return { ...c, database: SheetEdit.insertSummaryRow(c.database, v.clientId, v.index) };
    }
    case "moveSummaryRow":
      return { ...c, database: SheetEdit.moveSummaryRow(c.database, resolveId(client, v.ws, v.summaryRowId), v.index) };
    case "deleteSummaryRow":
      return { ...c, database: SheetEdit.removeSummaryRow(SheetEdit.removeSummaryRow(c.database, v.summaryRowId), resolveId(client, v.ws, v.summaryRowId)) };
  }
}

function isDbMutation(m: Mutation<unknown, Error, unknown, unknown>): boolean {
  const key = m.options.mutationKey;
  return Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("db.");
}

/** Writes not yet confirmed (in flight, waiting to retry, or paused offline), oldest first. */
function pendingWrites(client: QueryClient, ws: string, databaseId?: string): DbVars[] {
  return client
    .getMutationCache()
    .getAll()
    .filter((m) => isDbMutation(m) && (m.state.status === "pending" || m.state.isPaused))
    .map((m) => m.state.variables as DbVars | undefined)
    .filter((v): v is DbVars => v !== undefined && v.ws === ws && (databaseId === undefined || v.databaseId === databaseId));
}

function entityId(v: DbVars): string {
  switch (v.kind) {
    case "createRow":
      return v.input.clientId;
    case "updateRow":
    case "deleteRow":
      return v.rowId;
    case "updateDatabase":
      return v.databaseId;
    case "createView":
      return v.input.clientId;
    case "updateView":
    case "deleteView":
      return v.viewId;
    case "writeCells":
    case "moveSummaryRow":
    case "deleteSummaryRow":
      return v.databaseId;
    case "createSummaryRow":
      return v.clientId;
  }
}

// ---------- Query ----------

async function allRows(api: ApiClient, ws: string, databaseId: string): Promise<Row[]> {
  const rows: Row[] = [];
  let cursor: string | undefined = undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const q: { limit: number; cursor?: string } = { limit: PAGE_LIMIT };
    if (cursor !== undefined) q.cursor = cursor;
    const page = await api.listRows(ws, databaseId, q);
    rows.push(...page.rows);
    if (page.nextCursor === undefined) break;
    cursor = page.nextCursor;
  }
  return rows;
}

/** Server state of a database with this device's pending writes re-applied on top. */
export async function fetchDatabase(client: QueryClient, api: ApiClient, ws: string, databaseId: string): Promise<DatabaseCache> {
  const [meta, rows] = await Promise.all([api.getDatabase(ws, databaseId), allRows(api, ws, databaseId)]);
  let cache: DatabaseCache = {
    database: meta.database,
    views: meta.views.toSorted((a, b) => Db.compareText(a.rank, b.rank) || Db.compareText(a.id, b.id)),
    rows: Db.sortByRank(rows),
  };
  for (const v of pendingWrites(client, ws, databaseId)) cache = applyWrite(client, cache, v);
  return cache;
}

// ---------- Mutations ----------

/** Validation/permission failures won't succeed on retry: drop the optimistic state (refetch). */
function rollback(client: QueryClient, v: DbVars, e: unknown): void {
  if (v.kind === "createRow") updateDb(client, v.ws, v.databaseId, (c) => ({ ...c, rows: Db.removeRow(c.rows, v.input.clientId) }));
  if (v.kind === "createView") updateDb(client, v.ws, v.databaseId, (c) => ({ ...c, views: Db.removeView(c.views, v.input.clientId) }));
  if (v.kind === "createSummaryRow") updateDb(client, v.ws, v.databaseId, (c) => ({ ...c, database: SheetEdit.removeSummaryRow(c.database, v.clientId) }));
  const kind = errorKind(e);
  if (kind !== "network") void client.invalidateQueries({ queryKey: dbKeys.database(v.ws, v.databaseId) });
}

export function registerDatabaseMutations(client: QueryClient, api: ApiClient): void {
  const optimistic = (v: DbVars) => updateDb(client, v.ws, v.databaseId, (c) => applyWrite(client, c, v));
  const common = { scope: DB_SCOPE, onMutate: optimistic, onError: (e: unknown, v: DbVars) => rollback(client, v, e) };

  client.setMutationDefaults(["db.createRow"], {
    ...common,
    mutationFn: (v: CreateRowVars) => {
      const req: CreateRowRequest = { values: v.input.values, clientId: v.input.clientId };
      if (v.input.afterId !== undefined) req.afterId = resolveId(client, v.ws, v.input.afterId);
      if (v.input.templateId !== undefined) req.templateId = v.input.templateId;
      return api.createRow(v.ws, v.databaseId, req);
    },
    onSuccess: (row: Row, v: CreateRowVars) => {
      remember(client, v.ws, v.input.clientId, row.id);
      updateDb(client, v.ws, v.databaseId, (c) => ({ ...c, rows: Db.replaceRow(c.rows, v.input.clientId, row) }));
    },
  });

  client.setMutationDefaults(["db.updateRow"], {
    ...common,
    mutationFn: (v: UpdateRowVars) => {
      const req: UpdateRowRequest = {};
      if (v.patch.values !== undefined) req.values = v.patch.values;
      if (v.patch.afterId !== undefined) req.afterId = resolveId(client, v.ws, v.patch.afterId);
      if (v.patch.beforeId !== undefined) req.beforeId = resolveId(client, v.ws, v.patch.beforeId);
      if (v.patch.icon !== undefined) req.icon = v.patch.icon;
      return api.updateRow(v.ws, resolveId(client, v.ws, v.rowId), req);
    },
    onSuccess: (row: Row, v: UpdateRowVars) => {
      // Later queued edits of the same row stay applied on top of the server's answer.
      updateDb(client, v.ws, v.databaseId, (c) => {
        let next: DatabaseCache = { ...c, rows: Db.upsertRow(c.rows, row) };
        for (const p of pendingWrites(client, v.ws, v.databaseId)) if (p !== v && p.kind === "updateRow" && resolveId(client, v.ws, p.rowId) === row.id) next = applyWrite(client, next, p);
        return next;
      });
    },
  });

  client.setMutationDefaults(["db.deleteRow"], {
    ...common,
    mutationFn: (v: DeleteRowVars) => api.deleteRow(v.ws, resolveId(client, v.ws, v.rowId)),
  });

  client.setMutationDefaults(["db.updateDatabase"], {
    ...common,
    mutationFn: (v: UpdateDatabaseVars) => api.updateDatabase(v.ws, v.databaseId, v.req),
    onSuccess: (database: import("@bundle/contracts").Database, v: UpdateDatabaseVars) => {
      updateDb(client, v.ws, v.databaseId, (c) => {
        let next: DatabaseCache = { ...c, database };
        for (const p of pendingWrites(client, v.ws, v.databaseId)) if (p !== v && p.kind === "updateDatabase") next = applyWrite(client, next, p);
        return next;
      });
    },
  });

  client.setMutationDefaults(["db.createView"], {
    ...common,
    mutationFn: (v: CreateViewVars) => api.createView(v.ws, v.databaseId, v.input.request),
    onSuccess: (view: DbView, v: CreateViewVars) => {
      remember(client, v.ws, v.input.clientId, view.id);
      updateDb(client, v.ws, v.databaseId, (c) => ({ ...c, views: Db.upsertView(Db.removeView(c.views, v.input.clientId), view) }));
    },
  });

  client.setMutationDefaults(["db.updateView"], {
    ...common,
    mutationFn: (v: UpdateViewVars) => api.updateView(v.ws, resolveId(client, v.ws, v.viewId), v.req),
    onSuccess: (view: DbView, v: UpdateViewVars) => {
      updateDb(client, v.ws, v.databaseId, (c) => {
        let next: DatabaseCache = { ...c, views: Db.upsertView(c.views, view) };
        for (const p of pendingWrites(client, v.ws, v.databaseId)) if (p !== v && p.kind === "updateView" && resolveId(client, v.ws, p.viewId) === view.id) next = applyWrite(client, next, p);
        return next;
      });
    },
  });

  /** The server's database (summary rows) and rows, with this device's later pending writes re-applied on top. */
  const settleSheet = (v: DbVars, database: import("@bundle/contracts").Database, rows: Row[]) =>
    updateDb(client, v.ws, v.databaseId, (c) => {
      let next: DatabaseCache = { ...c, database };
      for (const r of rows) next = { ...next, rows: Db.upsertRow(next.rows, r) };
      for (const p of pendingWrites(client, v.ws, v.databaseId)) if (p !== v && (p.kind === "writeCells" || p.kind === "createSummaryRow" || p.kind === "moveSummaryRow" || p.kind === "deleteSummaryRow" || p.kind === "updateRow" || p.kind === "updateDatabase")) next = applyWrite(client, next, p);
      return next;
    });

  client.setMutationDefaults(["db.writeCells"], {
    ...common,
    mutationFn: (v: WriteCellsVars) => api.writeCells(v.ws, v.databaseId, { writes: resolveWrites(client, v.ws, v.writes) }, v.clientId),
    onSuccess: (res: WriteCellsResponse, v: WriteCellsVars) => settleSheet(v, res.database, res.rows),
  });

  client.setMutationDefaults(["db.createSummaryRow"], {
    ...common,
    mutationFn: (v: CreateSummaryRowVars) => api.createSummaryRow(v.ws, v.databaseId, v.index === undefined ? {} : { index: v.index }, v.clientId),
    onSuccess: (res: SummaryRowResponse, v: CreateSummaryRowVars) => {
      if (res.summaryRow !== undefined) remember(client, v.ws, v.clientId, res.summaryRow.id);
      settleSheet(v, res.database, []);
    },
  });

  client.setMutationDefaults(["db.moveSummaryRow"], {
    ...common,
    mutationFn: (v: MoveSummaryRowVars) => api.moveSummaryRow(v.ws, v.databaseId, resolveId(client, v.ws, v.summaryRowId), v.index),
    onSuccess: (res: SummaryRowResponse, v: MoveSummaryRowVars) => settleSheet(v, res.database, []),
  });

  client.setMutationDefaults(["db.deleteSummaryRow"], {
    ...common,
    mutationFn: (v: DeleteSummaryRowVars) => api.deleteSummaryRow(v.ws, v.databaseId, resolveId(client, v.ws, v.summaryRowId)),
    onSuccess: (res: SummaryRowResponse, v: DeleteSummaryRowVars) => settleSheet(v, res.database, []),
  });

  client.setMutationDefaults(["db.deleteView"], {
    ...common,
    mutationFn: (v: DeleteViewVars) => api.deleteView(v.ws, resolveId(client, v.ws, v.viewId)),
  });
}

/** Queues a write (optimistic now, sent when online, persisted across reloads). */
export function enqueue(client: QueryClient, v: DbVars): void {
  new MutationObserver<unknown, unknown, DbVars>(client, { mutationKey: [`db.${v.kind}`], scope: DB_SCOPE }).mutate(v).catch(() => {
    // Rolled back in onError; network failures stay queued.
  });
}

// ---------- Realtime ----------

/** Applies a row/view/database event to the cached database it belongs to (if cached). */
export function applyDatabaseEvent(client: QueryClient, ws: string, ev: PersistentEvent, meId: string): void {
  const databaseId = Db.eventDatabaseId(ev);
  if (databaseId === undefined) return;
  updateDb(client, ws, databaseId, (c) => {
    if (ev.type === "row.created") {
      const creating = pendingWrites(client, ws, databaseId).filter((v) => v.kind === "createRow").map((v) => (v as CreateRowVars).input.clientId);
      const temp = Db.matchPendingRow(c.rows, ev.row, creating, meId);
      if (temp !== undefined) {
        remember(client, ws, temp, ev.row.id);
        return { ...c, rows: Db.replaceRow(c.rows, temp, ev.row) };
      }
    }
    let next = Db.applyDbEvent(c, ev);
    // Keep my unconfirmed edits of that row/view on top of someone else's (or an older) state.
    if (next !== c && (ev.type === "row.updated" || ev.type === "view.updated" || ev.type === "database.updated")) {
      for (const v of pendingWrites(client, ws, databaseId)) {
        if (v.kind === "updateRow" && ev.type === "row.updated" && resolveId(client, ws, v.rowId) === ev.row.id) next = applyWrite(client, next, v);
        if (v.kind === "updateView" && ev.type === "view.updated" && resolveId(client, ws, v.viewId) === ev.view.id) next = applyWrite(client, next, v);
        if (v.kind === "updateDatabase" && ev.type === "database.updated") next = applyWrite(client, next, v);
        if (v.kind === "writeCells" && (ev.type === "row.updated" || ev.type === "database.updated")) next = applyWrite(client, next, v);
        if ((v.kind === "createSummaryRow" || v.kind === "moveSummaryRow" || v.kind === "deleteSummaryRow") && ev.type === "database.updated") next = applyWrite(client, next, v);
      }
    }
    return next;
  });
}

// ---------- Pending ----------

/** Ids with unconfirmed writes for one database (rows, views, the database), updated as mutations settle. */
export function pendingObservable(client: QueryClient, ws: string, databaseId: string): Observable<string[]> {
  let last: string[] = [];
  const compute = (): string[] => {
    const ids = pendingWrites(client, ws, databaseId).map((v) => entityId(v));
    const unique = [...new Set(ids.flatMap((id) => [id, resolveId(client, ws, id)]))];
    if (unique.length === last.length && unique.every((x, i) => x === last[i])) return last;
    last = unique;
    return last;
  };
  compute();
  return {
    subscribe: (listener) =>
      client.getMutationCache().subscribe(() => {
        const before = last;
        if (compute() !== before) listener();
      }),
    getSnapshot: () => last,
  };
}
